// Single source of truth for supported locales.
//
// Frontend mirror of backend `application.services.locale_service`. Keep
// `SUPPORTED_LOCALES` and `FALLBACK_LOCALE` in lock-step with the backend
// (`SUPPORTED_LANGUAGES`, `FALLBACK_LANGUAGE`) — they must agree so the same
// value can round-trip from cookie → API → DB → response.
//
// Exam configs / interview APIs use uppercase `EN`/`FR` — convert via
// `normalizeExamLanguage` / `examLanguageToLocale` in `@/utils/locale`.

export const SUPPORTED_LOCALES = ['en', 'fr'] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

export const FALLBACK_LOCALE: Locale = 'en';

export const NAMESPACES = [
  'common',
  'auth',
  'dashboard',
  'job',
  'candidates',
  'interview',
  'cv_upload',
  'settings',
  'marketing',
  'agents',
  'legal',
  'talentPool',
  'chatbot',
] as const;
export type Namespace = (typeof NAMESPACES)[number];

export const DEFAULT_NAMESPACE: Namespace = 'common';

// Cookie + localStorage key used by the public globe + backend lookups.
// Must match what the backend reads for anonymous flows.
export const LOCALE_STORAGE_KEY = 'invirtus_locale';

/** Workspace default — restored when leaving anonymous public candidate pages. */
export const TENANT_LOCALE_STORAGE_KEY = 'tenant_locale';

export const LOCALE_LABELS: Record<Locale, string> = {
  en: 'EN',
  fr: 'FR',
};

export const LOCALE_FULL_NAMES: Record<Locale, string> = {
  en: 'English',
  fr: 'Français',
};

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

export function normalizeLocale(value: unknown): Locale {
  if (typeof value !== 'string') return FALLBACK_LOCALE;
  const head = value.trim().toLowerCase().replace(/_/g, '-').split('-')[0];
  return isLocale(head) ? head : FALLBACK_LOCALE;
}
